import Breadcrumb from '@components/common/breadcrumb'
import FixedMobileCTA from '@components/course-landing-page/fixed-mobile-cta'
import { FastlyImage } from '@components/common/image'
import Ratings from '@components/common/ratings'
import PriceSlotsOverview from '@components/home/price-slots-overview'
import StudentCountriesOverview from '@components/home/student-countries-overview'
import PhoneInputContainer from '@containers/phone-input'
import Partners from '@components/locale-pages/home/us/partners'
import { t, Trans } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import { Monitor, BookOpen, TrendingUp } from 'react-feather'

const BREADCRUMB_ITEMS = [
  { name: 'Codingal', link: '/' },
  { name: 'Courses', link: '/courses' },
  { name: 'Math', link: '/courses/math' },
]

const FirstFold = () => {
  const [isFixedMobileCTAVisible, setIsFixedMobileCTAVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const showFixedCTAOnScroll = (event: any) => {
    if (!containerRef.current) return
    const { offsetTop, offsetHeight } = containerRef.current
    const scrollTopPosition = event.target.documentElement.scrollTop
    setIsFixedMobileCTAVisible(offsetTop + offsetHeight < scrollTopPosition)
  }

  useEffect(() => {
    document.getElementById('phone').focus()
    window.addEventListener('scroll', showFixedCTAOnScroll)
    return () => window.removeEventListener('scroll', showFixedCTAOnScroll)
  }, [])

  return (
    <>
      <FixedMobileCTA hideBtn={isFixedMobileCTAVisible} />
      <div
        className="relative flex justify-between px-5 md:px-3 pt-8 overflow-visible lg:rounded-b-lg"
        ref={containerRef}
      >
        <div className="relative container flex flex-col gap-y-5 mx-auto">
          <div className="self-start">
            <Breadcrumb items={BREADCRUMB_ITEMS} isInCoursePage />
          </div>
          <div className="flex flex-col text-grey z-10 lg:flex-row gap-y-5">
            <div className="z-10 flex flex-col items-center gap-y-5 text-center lg:items-start lg:text-left">
              <h1 className="max-w-2xl text-center font-600 lg:text-left custom-home-h1">
                <Trans>
                  Top-rated{' '}
                  <span className="underlined-text">online Math classes</span>{' '}
                  for kids to become the problem-solvers of tomorrow
                </Trans>
              </h1>

              <div className="flex flex-col text-base lg:text-xl items-start gap-2">
                <p className="mb-1 flex justify-start">
                  <Monitor
                    size={20}
                    className="inline mr-2 lg:mr-3 text-orange"
                  />
                  <span className="text-left -mt-1 leading-tight">
                    <Trans>
                      Engaging live 1:1 video lessons with a dedicated tutor
                    </Trans>
                  </span>
                </p>

                <p className="mb-1 flex justify-start">
                  <BookOpen
                    size={20}
                    className="inline mr-2 lg:mr-3 text-orange"
                  />
                  <span className="text-left -mt-1 leading-tight">
                    <Trans>
                      Matched to your child&apos;s school curriculum, level, and
                      pace
                    </Trans>
                  </span>
                </p>

                <p className="mb-1 flex justify-start">
                  <TrendingUp
                    size={20}
                    className="inline mr-2 lg:mr-3 text-orange"
                  />
                  <span className="text-left -mt-1 leading-tight">
                    <Trans>Boost confidence with measurable progress</Trans>
                  </span>
                </p>
              </div>

              <div className="text-left w-full sm:w-auto pt-3">
                <PhoneInputContainer
                  isErrorDarkRed
                  buttonText={t`Try a free lesson`}
                  isInHomePage
                  isInputFieldBorderGrey
                  courseQuery="math-trial-class"
                />
                <div className="mt-3 mb-3 lg:mb-8">
                  <PriceSlotsOverview />
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:space-x-8 space-y-5 md:space-y-0 justify-center lg:justify-start pb-6 lg:pb-0">
                <StudentCountriesOverview isTextWhite={false} />
                <Ratings isDarkText />
              </div>
            </div>

            <div className="relative flex flex-col items-center justify-center mx-auto lg:max-w-half">
              <FastlyImage
                src="/images/courses/math/first-fold.png"
                width={556}
                height={379}
                alt={t`Children learning Math through online classes`}
                priority
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="relative bg-orange-100 my-12">
        <Partners />
      </div>
    </>
  )
}

export default FirstFold
